import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { api } from '../api.js';
import { EVENTS, eventByKey } from '@shared/events.js';
import { formatTime, formatDate, timeAgo } from '@shared/format.js';

export default function StudentHome() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    const ev = user?.main_event || '3x3';
    Promise.all([
      api(`/stats/overview?event=${ev}`),
      api(`/sessions?limit=6`),
      api(`/stats/comments`),
    ])
      .then(([o, s, c]) => {
        setOverview(o.overview);
        setSessions(s.sessions);
        setComments(c.comments);
      })
      .catch(() => {});
  }, [user?.main_event]);

  const ev = eventByKey(user?.main_event || '3x3');
  const startedAt = user?.started_at ? new Date(user.started_at) : null;
  const daysLearned = startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 86400000)) + 1
    : null;

  return (
    <div className="space-y-5">
      {/* 欢迎横幅 */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-ink-800 via-ink-800 to-accent/20 border border-slate-700/50 p-6 sm:p-8">
        <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-accent/10 blur-2xl" />
        <div className="relative">
          <p className="text-sm text-slate-400">欢迎回来，{user?.nickname}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mt-1">
            {overview?.today_solves > 0 ? (
              <>今天已完成 <span className="text-accent">{overview.today_solves}</span> 把{ev.name}速拧</>
            ) : (
              <>今天还没打卡，来一组成绩吧</>
            )}
          </h1>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/timer" className="btn-primary">去计时</Link>
            <Link to="/data" className="btn-ghost">查看成长曲线</Link>
          </div>
        </div>
      </div>

      {/* 关键指标 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="连续打卡" value={`${overview?.streak ?? 0} 天`} />
        <Metric label="单次 PB" value={overview?.pb_single != null ? formatTime(overview.pb_single) : '—'} accent />
        <Metric label="当前 Ao5" value={overview?.current_ao5 != null ? formatTime(overview.current_ao5) : '—'} />
        <Metric label="累计成绩" value={`${overview?.total_solves ?? 0} 把`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* 最近打卡 */}
        <div className="lg:col-span-2 card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-100">最近打卡</h2>
            <Link to="/data" className="text-xs text-accent hover:underline">查看全部 ›</Link>
          </div>
          {sessions.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">
              还没有打卡记录，点击右上角「去计时」开始第一次训练
            </div>
          ) : (
            <ul className="divide-y divide-slate-700/40">
              {sessions.slice(0, 5).map((s) => (
                <li key={s.id} className="py-3 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-slate-700/40 flex items-center justify-center text-lg">
                    {EVENTS.find((e) => e.key === s.event_type)?.name.slice(0, 2) || s.event_type}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200">
                      {EVENTS.find((e) => e.key === s.event_type)?.name || s.event_type}
                      <span className="text-slate-500 ml-2">{s.count} 把</span>
                    </div>
                    <div className="text-xs text-slate-500">{formatDate(s.created_at, { month: 'long', day: 'numeric' })} · {timeAgo(s.created_at)}</div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="font-mono text-sm text-slate-100">
                      {s.avg != null ? formatTime(s.avg) : '—'}
                    </div>
                    <div className="text-[11px] text-slate-500">平均</div>
                  </div>
                  {s.comments?.length > 0 && (
                    <span className="text-xs bg-accent/15 text-accent rounded-full px-2 py-0.5" title={s.comments[0].content}>
                      教练点评
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 侧栏：教练留言 + 学习信息 */}
        <div className="space-y-5">
          <div className="card p-5">
            <h2 className="font-semibold text-slate-100 mb-3">教练留言</h2>
            {comments.length === 0 ? (
              <p className="text-sm text-slate-500">暂无留言</p>
            ) : (
              <ul className="space-y-3">
                {comments.slice(0, 3).map((c) => (
                  <li key={c.id} className="text-sm bg-slate-700/25 rounded-xl p-3">
                    <p className="text-slate-300 leading-relaxed">{c.content}</p>
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      {c.coach_name} · {timeAgo(c.created_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card p-5 text-sm space-y-2 text-slate-400">
            <h2 className="font-semibold text-slate-100">学习档案</h2>
            <p>主项目：<span className="text-slate-200">{ev.name}</span></p>
            <p>开始学习：<span className="text-slate-200">{startedAt ? formatDate(startedAt) : '未设置'}</span></p>
            {daysLearned != null && <p>已坚持：<span className="text-accent">{daysLearned} 天</span></p>}
            <p>打卡频率目标：<span className="text-slate-200">{user?.checkin_freq === 'weekly' ? '每周' : '每日'}</span></p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, accent = false }) {
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-semibold ${accent ? 'text-accent' : 'text-slate-100'}`}>{value}</div>
    </div>
  );
}
